import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// @mui
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Divider from '@mui/material/Divider';
// components
import Iconify from 'src/components/iconify';
// utils
import axios, { API_ENDPOINTS } from 'src/utils/axios';
//
import { useOnboarding } from './onboarding-context';

// Help menu supporting dialogs (§7): glossary, shortcuts, what's new,
// checklist, and the "all tours" picker. Each is deliberately small and
// self-contained — opened one at a time from help-popover.js.

export function GlossaryDialog({ open, onClose }) {
  const [terms, setTerms] = useState([]);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    axios.get(API_ENDPOINTS.onboarding.glossary).then((res) => setTerms(res.data?.data || [])).catch(() => setTerms([]));
  }, [open]);

  const filtered = terms.filter((t) => t.term.toLowerCase().includes(query.toLowerCase()));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Glossary</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size="small"
          placeholder="Search terms…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Iconify icon="eva:search-fill" /></InputAdornment> }}
        />
        <List dense>
          {filtered.map((g) => (
            <ListItem key={g.term} disableGutters sx={{ display: 'block', mb: 1.5 }}>
              <Typography variant="subtitle2">{g.term}</Typography>
              <Typography variant="body2" color="text.secondary">{g.definition}</Typography>
            </ListItem>
          ))}
          {!filtered.length && <Typography variant="body2" color="text.secondary">No terms match "{query}".</Typography>}
        </List>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
GlossaryDialog.propTypes = { open: PropTypes.bool, onClose: PropTypes.func };

export function ShortcutsDialog({ open, onClose }) {
  const [shortcuts, setShortcuts] = useState([]);

  useEffect(() => {
    if (!open) return;
    axios.get(API_ENDPOINTS.onboarding.shortcuts).then((res) => setShortcuts(res.data?.data || [])).catch(() => setShortcuts([]));
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Keyboard shortcuts</DialogTitle>
      <DialogContent>
        <List dense>
          {shortcuts.map((s) => (
            <ListItem key={s.keys} disableGutters>
              <ListItemText
                primary={<Chip size="small" label={s.keys} sx={{ fontFamily: 'monospace' }} />}
                secondary={s.description}
                sx={{ '& .MuiListItemText-primary': { mb: 0.5 } }}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
ShortcutsDialog.propTypes = { open: PropTypes.bool, onClose: PropTypes.func };

export function WhatsNewDialog({ open, onClose }) {
  const { markWhatsNewSeen } = useOnboarding();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!open) return;
    axios.get(API_ENDPOINTS.onboarding.whatsNew).then((res) => setItems(res.data?.data || [])).catch(() => setItems([]));
    markWhatsNewSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>What&apos;s new</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} divider={<Divider flexItem />}>
          {items.slice().reverse().map((item) => (
            <Stack key={item.title} spacing={0.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle2">{item.title}</Typography>
                <Typography variant="caption" color="text.disabled">{item.date}</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">{item.body}</Typography>
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
WhatsNewDialog.propTypes = { open: PropTypes.bool, onClose: PropTypes.func };

export function ChecklistDialog({ open, onClose }) {
  const { state, dismissChecklistStep, refresh } = useOnboarding();

  useEffect(() => {
    if (open) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const checklist = state?.checklist || [];
  const dismissedKeys = state?.checklistDismissedKeys || [];
  const visible = checklist.filter((c) => c.done || !dismissedKeys.includes(c.key));
  const doneCount = checklist.filter((c) => c.done).length;
  const pct = checklist.length ? Math.round((doneCount / checklist.length) * 100) : 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Getting started</DialogTitle>
      <DialogContent>
        <Stack spacing={0.5} sx={{ mb: 2.5 }}>
          <Typography variant="body2" color="text.secondary">{doneCount} of {checklist.length} done</Typography>
          <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 1 }} />
        </Stack>
        <List dense>
          {visible.map((step) => (
            <ListItem
              key={step.key}
              disableGutters
              secondaryAction={!step.done && (
                <Button size="small" onClick={() => dismissChecklistStep(step.key)}>Dismiss</Button>
              )}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Checkbox edge="start" checked={step.done} disabled tabIndex={-1} />
              </ListItemIcon>
              <ListItemText
                primary={step.label}
                secondary={step.help}
                sx={step.done ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
ChecklistDialog.propTypes = { open: PropTypes.bool, onClose: PropTypes.func };

export function AllToursDialog({ open, onClose }) {
  const { tours, state, startTour, completeTour } = useOnboarding();
  const completedKeys = state?.completedTourKeys || [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Tours</DialogTitle>
      <DialogContent>
        <List dense>
          {tours.map((tour) => {
            const done = completedKeys.includes(tour.key);
            return (
              <ListItemButton
                key={tour.key}
                onClick={() => { startTour(tour.key); onClose(); }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Iconify icon={done ? 'solar:check-circle-bold' : 'solar:play-circle-bold-duotone'} width={22} />
                </ListItemIcon>
                <ListItemText primary={tour.label} secondary={`${tour.steps.length} steps${done ? ' · completed' : ''}`} />
                {done && (
                  <Button size="small" onClick={(e) => { e.stopPropagation(); completeTour(tour.key); }}>
                    Replay
                  </Button>
                )}
              </ListItemButton>
            );
          })}
          {!tours.length && <Typography variant="body2" color="text.secondary">No tours available yet.</Typography>}
        </List>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
AllToursDialog.propTypes = { open: PropTypes.bool, onClose: PropTypes.func };
